import { Box } from '@mui/material';
import { styles, colors, Typography, IconGraphy, Tooltip } from '@leapeasy/ui-kit';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { ReportCard } from 'elements/ReportCard';

const BUILDING_LIST_PATH = '/property/properties';

/**
 * Unit tiles come from Apartment Building (property) Salesforce fields, not Policies Active.
 * Total  = SUM(Total_of_Units__c)
 * Active = SUM(Total_of_Active_Leap_Units__c)  — Leap-covered units on the property
 * Inactive = Total − Active  (portal math; there is no Inactive Salesforce field)
 */
const TOTAL_UNITS_TOOLTIP =
  'All units on the properties in this view, added together. Comes from each property’s Total of Units field.';
const ACTIVE_UNITS_TOOLTIP =
  'Units Leap counts as covered on each property, added together. Comes from each property’s Total of Active Leap Units field. This is not the Active Yes/No column on the Policies table.';
const INACTIVE_UNITS_TOOLTIP =
  'Total Unit Count minus Active Unit Count. Units on these properties that are not counted as Leap-covered. There is no separate Salesforce Inactive field.';

const formatCount = (value) => Number(value ?? 0).toLocaleString();

const parseMetric = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const panelSx = {
  borderRadius: `${styles.borderRadius.large}px`,
  border: `1px solid ${colors.black[300]}`,
  background: 'white',
  overflow: 'hidden',
  flex: '1 1 420px',
  minWidth: 0,
  maxWidth: '100%',
  padding: '16px',
  boxSizing: 'border-box',
};

const nestedRowSx = {
  display: 'flex',
  gap: '16px',
  mt: 1,
  width: '100%',
  minWidth: 0,
  alignItems: 'stretch',
};

const nestedCellSx = {
  flex: '1 1 0%',
  minWidth: 0,
  display: 'flex',
  alignItems: 'stretch',
};

const headerRowSx = {
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  cursor: 'pointer',
  minWidth: 0,
  '&:hover': { opacity: 0.75 },
};

export const BuildingCount = ({ data = {} }) => {
  const navigate = useNavigate();

  const openAllProperties = () => {
    navigate(BUILDING_LIST_PATH);
  };

  const openFilteredProperties = (searchParams) => {
    navigate({
      pathname: BUILDING_LIST_PATH,
      search: createSearchParams(searchParams).toString(),
      state: searchParams,
    });
  };

  // Production fields from /report/buildingcount
  const totalBuildings = parseMetric(data.totalBuildings);
  const suretyBuildings = parseMetric(data.EPBuildings);
  const commercialBuildings = parseMetric(data.AEBuidings);
  const totalUnits = parseMetric(data.totalUnits);
  const leapUnits = parseMetric(data.leapUnits);
  const inactiveUnits = Math.max(0, totalUnits - leapUnits);

  return (
    <Box display="flex" flexWrap="wrap" gap="20px" sx={{ width: '100%', minWidth: 0 }}>
      {/* Properties panel — production building counts + filters */}
      <Box sx={panelSx}>
        <Box sx={headerRowSx} onClick={openAllProperties}>
          <Typography
            variant="body2"
            style={{ color: colors.neutral[900], minWidth: 0, flexShrink: 1 }}
          >
            Total Property Count
          </Typography>
          <Box sx={{ flexGrow: 1, minWidth: 8 }} />
          <Typography
            variant="h3"
            style={{ fontWeight: '500', color: colors.purple[900], flexShrink: 0 }}
          >
            {formatCount(totalBuildings)}
          </Typography>
          <IconGraphy icon="Arrow.NorthEast" style={{ color: '#867B8D', flexShrink: 0 }} />
        </Box>

        <Box sx={nestedRowSx}>
          <Box sx={nestedCellSx}>
            <Tooltip
              title="Rent Guaranty properties (Event Process)."
              showCarot
              placement="top"
              variant="contained"
              fullWidth
            >
              <ReportCard
                title="Surety"
                count={formatCount(suretyBuildings)}
                onClick={() => openFilteredProperties({ propertyType: 'Event Process' })}
              />
            </Tooltip>
          </Box>

          <Box sx={nestedCellSx}>
            <Tooltip
              title="Commercial properties (Guarantor Waiver Program / Auto Enroll)."
              showCarot
              placement="top"
              variant="contained"
              fullWidth
            >
              <ReportCard
                title="Commercial"
                count={formatCount(commercialBuildings)}
                onClick={() => openFilteredProperties({ propertyType: 'Auto Enroll' })}
              />
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Units panel — SUM of property Salesforce fields; Inactive is Total − Active */}
      <Box sx={panelSx}>
        <Tooltip
          title={TOTAL_UNITS_TOOLTIP}
          showCarot
          placement="top"
          variant="contained"
          fullWidth
        >
          <Box sx={headerRowSx} onClick={openAllProperties}>
            <Typography
              variant="body2"
              style={{ color: colors.neutral[900], minWidth: 0, flexShrink: 1 }}
            >
              Total Unit Count
            </Typography>
            <Box sx={{ flexGrow: 1, minWidth: 8 }} />
            <Typography
              variant="h3"
              style={{ fontWeight: '500', color: colors.purple[900], flexShrink: 0 }}
            >
              {formatCount(totalUnits)}
            </Typography>
            <IconGraphy icon="Arrow.NorthEast" style={{ color: '#867B8D', flexShrink: 0 }} />
          </Box>
        </Tooltip>

        <Box sx={nestedRowSx}>
          <Box sx={nestedCellSx}>
            <Tooltip
              title={ACTIVE_UNITS_TOOLTIP}
              showCarot
              placement="top"
              variant="contained"
              fullWidth
            >
              <ReportCard
                title="Active Unit Count"
                count={formatCount(leapUnits)}
                onClick={() => openFilteredProperties({ unitFilter: 'active' })}
              />
            </Tooltip>
          </Box>

          <Box sx={nestedCellSx}>
            <Tooltip
              title={INACTIVE_UNITS_TOOLTIP}
              showCarot
              placement="top"
              variant="contained"
              fullWidth
            >
              <ReportCard
                title="Inactive Unit Count"
                count={formatCount(inactiveUnits)}
                onClick={() => openFilteredProperties({ unitFilter: 'inactive' })}
              />
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
